/**
 * Recordings Zustand store.
 *
 * Not persisted — the list comes from a fetch. UI prefs (sort order, etc.)
 * are kept out of v1 entirely; if added later, persist them under a
 * `recordings_ui_prefs`-style key (NOT `lvdr_session_*`, since they aren't
 * account-tied).
 *
 * Status correlation lives in a `Map<recordingId, timeout>` per the
 * CLAUDE.md rule: "WebSocket load states must have a timeout path to
 * 'error'." Module-level slots are explicitly forbidden — two concurrent
 * uploads would clobber each other's timers.
 */

import React from 'react';

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { toast } from 'sonner';
import { getConfig } from '@/lib/config';
import { isPlanUpgradeRequired } from '@/lib/errors/catalog';
import { HttpError } from '@/lib/errors/http';
import { notifySubscriptionRequired } from '@/lib/http';
import { isSessionEnding } from '@/lib/auth/sessionEnding';
import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';
import { translateImperative } from '@/lib/i18n/imperative';
import { useToastStore, type WireError } from '@/stores/toastStore';

// Bridge shorthand for this store's user-facing copy (R8 Phase 1c) — resolves
// `recordings.store.<key>` in the active locale, falling back to the English
// literal when the key is missing from every catalog.
const tRec = (
  key: string,
  fallback: string,
  values?: Record<string, string | number>,
): string => translateImperative(`recordings.store.${key}`, values) ?? fallback;
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import type {
  CancelTranscriptionResponse,
  DeleteRecordingResponse,
  GetRecordingResponse,
  ListRecordingsResponse,
  PromoteRecordingResponse,
  Recording,
  RecordingStatusEvent,
  RetryTranscriptionResponse,
  UpdateRecordingResponse,
} from '@/lib/protobuf/gen/recordings/recordings_pb';
import { wsManager } from '@/lib/websocket';
import {
  importFromURL,
  sendCancelTranscription,
  sendDeleteRecording,
  sendListRecordings,
  sendPromoteRecording,
  sendRetryTranscription,
  sendUpdateRecording,
  RateLimitedError,
  SessionLockedError,
  UnavailableError,
  setSelfSpeaker as apiSetSelfSpeaker,
  uploadRecording,
  type UpdateRecordingFields,
} from './api';
import { useWorkspaceStore } from '@/features/workspaces/workspaceStore';
import { showCreditsExhaustedToast } from '@/features/budget/exhaustedToast';
import { readAudioDurationSecs } from './audioDuration';

// E1 Phase 8: the workspace to attribute this ASR spend to (the session's active
// workspace), or undefined for personal/org-pool. Read at call time so it always
// reflects the current selection.
function activeWorkspaceId(): string | undefined {
  return useWorkspaceStore.getState().activeWorkspaceId ?? undefined;
}

/**
 * Cadence at which we re-poll authoritative server state for an in-flight
 * recording (a fallback for a missed/late WS status event).
 */
const STATUS_POLL_MS = 30_000;

/**
 * Margin we wait PAST the server's own `LVDR_ASR_MAX_WAIT` before declaring an
 * in-flight recording stuck. The server marks a genuinely stalled job `failed`
 * at its cap, so this margin lets our poll observe that terminal state first;
 * we only fabricate a failure if the server has gone silent entirely (WS dead
 * AND list-refetch unanswered).
 */
const STATUS_MAX_WAIT_MARGIN_SECS = 5 * 60;

/**
 * Hard ceiling on how long we keep an in-flight recording "alive" before
 * declaring it stuck. Derived from the server's configured ASR wait
 * (`asrMaxWaitSecs`, mirrored from backend `LVDR_ASR_MAX_WAIT`) + the margin,
 * so client and server can't drift when an operator changes the cap. Robust
 * to a missing/garbled config (SSR, tests) via the 30-min default.
 */
function statusMaxWaitMs(): number {
  let secs = 1800;
  try {
    const configured = getConfig().asrMaxWaitSecs;
    if (typeof configured === 'number' && configured > 0) secs = configured;
  } catch {
    // config not ready (server render / unit test without config mock) —
    // keep the default; the watchdog is a fallback, not load-bearing.
  }
  return (secs + STATUS_MAX_WAIT_MARGIN_SECS) * 1000;
}

/**
 * Statuses that are still in-flight — covered by the poll/ceiling fallback.
 *
 * `extracting` is here for the same reason it is in CANCELLABLE_STATUSES below:
 * the backend declares it (`internal/recordings/models.go`) but nothing writes
 * it yet. Including it costs nothing today and means that whoever wires the
 * writer does not ship a row with a Cancel button, no badge, no spinner and no
 * liveness watchdog — the shape this file's own comment warns about.
 */
const IN_FLIGHT_STATUSES = new Set([
  'pending',
  'queued',
  'uploading',
  'extracting',
  'transcribing',
  'processing',
]);

/**
 * The statuses `HandleCancelTranscription` accepts, verbatim
 * (`internal/recordings/ws_handler.go` — pending / extracting / transcribing).
 *
 * Deliberately NOT `IN_FLIGHT_STATUSES`: that set exists for the client-side
 * liveness watchdog and additionally contains `uploading` (client-local — the
 * server has no row to cancel yet) plus `queued`/`processing` (not statuses the
 * server writes for recordings). Gating the button on it would ship an
 * affordance the backend rejects.
 *
 * The shared seam every gate site uses is the `canCancelTranscription` helper
 * one line below, NOT this set: RecordingList (cards + rows) and RecordingPlayer
 * all import the helper, so they cannot drift apart. The `export` here is for
 * the tests that assert the set's membership directly — production code should
 * keep going through the helper.
 */
export const CANCELLABLE_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'extracting',
  'transcribing',
]);

/** True when the server would accept a cancel for this status. */
export function canCancelTranscription(status: string): boolean {
  return CANCELLABLE_STATUSES.has(status);
}

/** The terminal status a user-requested cancel lands on. */
export const STATUS_CANCELLED = 'cancelled';

/**
 * Terminal statuses after which the list is re-fetched. RecordingStatusEvent
 * carries only {recording_id, status, failure_reason}; transcript, speakers,
 * asr_model, cost_usd and duration_secs are populated server-side and would
 * otherwise stay at their pre-transcription zero values until a manual refresh.
 */
const TERMINAL_REFETCH_STATUSES = new Set([
  'ready',
  'completed',
  'failed',
  'error',
  STATUS_CANCELLED,
]);

/**
 * The failure reason a status transition should leave on the row.
 *
 * The `evReason || stored` carry-forward is right for every transition except a
 * cancel: a terminal event often ships an empty reason and the stored one is the
 * better copy. A cancel is a deliberate user action, not an error, and the
 * server clears FailureReason on the way to `cancelled` — so carrying forward
 * would leave red failure text from an earlier attempt sitting next to a neutral
 * "cancelled" badge.
 */
export function resolveFailureReason(
  nextStatus: string,
  evReason: string,
  stored: string | undefined,
): string {
  if (nextStatus === STATUS_CANCELLED) return '';
  return evReason || stored || '';
}

/**
 * When we first started watching an in-flight recording. Module-level (not
 * Zustand state) like `_pendingBlobs` — it's bookkeeping, not UI. Persists
 * across the repeated reschedules so the ceiling measures TOTAL wait, not
 * time-since-last-event. Cleared when the row goes terminal.
 */
const _watchStartedAt = new Map<string, number>();

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Held captures that failed to upload because the session was locked (HTTP
 * 423) — typically a mobile screen-lock suspended the WS mid-recording. The
 * blob bytes live in a MODULE-LEVEL map (not Zustand state) so immer never
 * tries to draft/freeze a Blob, and so the heavy bytes stay out of devtools
 * snapshots. The reactive `pendingUploads[]` carries only metadata for the UI.
 *
 * Durability caveat: in-memory only. If Android discards the backgrounded tab
 * during a long lock, the held blob is lost on reload. Surviving that needs
 * IndexedDB persistence — a follow-up, and a security tradeoff (plaintext
 * audio at rest) since the DEK is exactly what's missing while locked.
 */
const _pendingBlobs = new Map<string, Blob>();

/**
 * BILL-05: one scheduled retry per `rate_limited` capture, fired when its
 * `Retry-After` elapses. A `Map` keyed by pending id — never a single slot, or
 * two rate-limited captures would clobber each other's timer. Cleared on
 * discard and whenever the entry is retried or rescheduled.
 */
const _rateLimitTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearRateLimitTimer(id: string): void {
  const t = _rateLimitTimers.get(id);
  if (t !== undefined) clearTimeout(t);
  _rateLimitTimers.delete(id);
}

/**
 * Keep a refused capture on the device (423 / 402 / 429 / 503 / 241): the bytes go into
 * `_pendingBlobs`, the metadata — including the duration hint it was captured
 * with — into `pendingUploads`. Returns the new pending id.
 */
function holdCapture(
  file: File,
  title: string | undefined,
  status: 'held' | 'needs_credits' | 'rate_limited' | 'needs_plan' | 'unavailable',
  durationSecs: number | undefined,
  retryAt?: number,
): string {
  const id = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  _pendingBlobs.set(id, file);
  ensureHeldCaptureUnloadGuard();
  useRecordingsStore.setState((s) => {
    s.pendingUploads.push({
      id,
      title: title || file.name,
      sizeBytes: file.size,
      capturedAt: Date.now(),
      status,
      ...(durationSecs !== undefined ? { durationSecs } : {}),
      ...(retryAt !== undefined ? { retryAt } : {}),
    });
  });
  return id;
}

/**
 * Arm the one scheduled retry for a `rate_limited` or `unavailable` capture. On firing it
 * clears `retryAt` first, so a timer that lands a hair before the recorded
 * instant is not turned away by `retryPendingUpload`'s not-yet-due guard.
 */
function scheduleRateLimitedRetry(id: string, retryAt: number): void {
  clearRateLimitTimer(id);
  const timer = setTimeout(
    () => {
      _rateLimitTimers.delete(id);
      useRecordingsStore.setState((s) => {
        const p = s.pendingUploads.find((x) => x.id === id);
        if (p) delete p.retryAt;
      });
      void useRecordingsStore.getState().actions.retryPendingUpload(id);
    },
    Math.max(0, retryAt - Date.now()),
  );
  _rateLimitTimers.set(id, timer);
}

/** Whole minutes until `retryAt`, at least 1 — for the rate-limit toast copy. */
function minutesUntil(retryAt: number): number {
  return Math.max(1, Math.ceil((retryAt - Date.now()) / 60_000));
}

/**
 * HTTP 402 from the upload routes: the server refused an upload it cannot
 * bill. NOT every 402 — BILL-03's subscription gate also answers 402, with
 * ERROR_CODE_SUBSCRIPTION_REQUIRED; that one is a paywall, and "add credits"
 * would send an unsubscribed user to buy a pack they cannot use.
 */
function isCreditsRefusal(err: unknown): err is HttpError {
  return err instanceof HttpError && err.status === 402 && !isSubscriptionRefusal(err);
}

/**
 * BILL-20f: the 241 (HTTP 403, ERROR_CODE_PLAN_UPGRADE_REQUIRED) for a plan
 * without recordings_asr. Recoverable by upgrading, like a 402 — NOT every
 * 403, which stays a drop.
 */
function isPlanRefusal(err: unknown): err is HttpError {
  return err instanceof HttpError && isPlanUpgradeRequired(err.wire);
}

/** BILL-03's 402: route to the paywall (raw fetch never reaches apiFetch's check). */
function isSubscriptionRefusal(err: unknown): err is HttpError {
  return err instanceof HttpError && err.status === 402 && err.wire.code === ErrorCode.SUBSCRIPTION_REQUIRED;
}

/**
 * Warn before unloading a tab that holds refused captures. The bytes live only
 * in `_pendingBlobs`, so a reload, a closed tab or the credit-pack checkout
 * redirect (same tab) loses them. Registered once, on the first hold; silent
 * during a logout (hardLogout marks the session ending before its redirect).
 */
let _unloadGuardInstalled = false;
function ensureHeldCaptureUnloadGuard(): void {
  if (_unloadGuardInstalled || typeof window === 'undefined') return;
  _unloadGuardInstalled = true;
  window.addEventListener('beforeunload', (e) => {
    if (_pendingBlobs.size === 0 || isSessionEnding()) return;
    e.preventDefault();
    // Legacy path (Safari, older Chrome) reads returnValue, not preventDefault.
    e.returnValue = '';
  });
}

const RATE_LIMITED_DESCRIPTION_EN =
  'Too many uploads in the last hour. The recording is saved on this device and will retry in about {minutes} min.';
const CREDITS_HELD_DESCRIPTION_EN =
  'Not enough credits to transcribe this recording. It is saved on this device — keep this tab open, add credits in the new tab, then retry.';

const PLAN_HELD_TITLE_EN = 'Not in your plan — recording saved on this device';
const PLAN_HELD_DESCRIPTION_EN =
  'Transcription is part of a higher plan. Keep this tab open, open See plans from the banner (new tab), upgrade, then retry.';

const UNAVAILABLE_HELD_DESCRIPTION_EN =
  'The service could not check your plan just now. The recording is saved on this device and will retry in about {minutes} min.';

/** BILL-20f P1: the toast for a capture kept after a 503 UNAVAILABLE. */
function showUnavailableHeldToast(retryAt: number): void {
  toast.warning(tRec('unavailableHeldTitle', 'Temporarily unavailable — recording saved on this device'), {
    description: tRec('unavailableHeldDescription', UNAVAILABLE_HELD_DESCRIPTION_EN, {
      minutes: minutesUntil(retryAt),
    }),
  });
}

function showRateLimitedToast(retryAt: number): void {
  toast.warning(tRec('rateLimitedTitle', 'Upload limit reached'), {
    description: tRec('rateLimitedDescription', RATE_LIMITED_DESCRIPTION_EN, { minutes: minutesUntil(retryAt) }),
  });
}

function showCreditsHeldToast(): void {
  // newTab: the capture is in this tab's memory, and checkout redirects the tab.
  showCreditsExhaustedToast(tRec('creditsHeldDescription', CREDITS_HELD_DESCRIPTION_EN), { newTab: true });
}

/**
 * BILL-20f: the toast for a capture held for the plan. Deliberately NO /plans
 * link: the generic upsell toast's link opens in the same tab and would drop
 * the in-memory capture. The banner's new-tab "See plans" is the only exit.
 */
function showPlanHeldToast(): void {
  toast.warning(tRec('planHeldTitle', PLAN_HELD_TITLE_EN), {
    description: tRec('planHeldDescription', PLAN_HELD_DESCRIPTION_EN),
  });
}

/**
 * The upload's refusal branches that KEEP the capture on the device: 423
 * (session locked — the bytes are fine, only the server-side DEK is missing;
 * typical trigger: a phone screen-lock suspended the WS mid-recording), 429
 * (over the per-user upload limit — retried once when Retry-After elapses,
 * never before), 402 (refused for credits — the user tops up on /billing and
 * retries from the banner) and 241 (refused for the plan, BILL-20f — the user
 * upgrades on /plans in a new tab and retries from the banner) and 503
 * UNAVAILABLE (the tier could not be read, BILL-20f P1 — transient, retried
 * once Retry-After elapses, default 60 s). Returns false
 * for every other failure, which the caller drops as before (BILL-05).
 */
function holdRefusedUpload(file: File, title: string | undefined, hint: number | undefined, err: unknown): boolean {
  if (isSubscriptionRefusal(err)) notifySubscriptionRequired();
  if (err instanceof SessionLockedError) {
    holdCapture(file, title, 'held', hint);
    toast.warning(tRec('heldTitle', 'Session locked — recording saved on this device'), {
      description: tRec(
        'heldDescription',
        'It will upload automatically when your session reconnects. Keep this tab open.',
      ),
    });
    return true;
  }
  if (err instanceof RateLimitedError) {
    const retryAt = Date.now() + err.retryAfterSecs * 1000;
    scheduleRateLimitedRetry(holdCapture(file, title, 'rate_limited', hint, retryAt), retryAt);
    showRateLimitedToast(retryAt);
    return true;
  }
  if (err instanceof UnavailableError) {
    // BILL-20f P1: the tier could not be read (503). Transient: keep the
    // capture and retry it once Retry-After (default 60 s) elapses.
    const retryAt = Date.now() + err.retryAfterSecs * 1000;
    scheduleRateLimitedRetry(holdCapture(file, title, 'unavailable', hint, retryAt), retryAt);
    showUnavailableHeldToast(retryAt);
    return true;
  }
  if (isCreditsRefusal(err)) {
    holdCapture(file, title, 'needs_credits', hint);
    showCreditsHeldToast();
    return true;
  }
  if (isPlanRefusal(err)) {
    // No onRetry: the banner owns the retry.
    holdCapture(file, title, 'needs_plan', hint);
    showPlanHeldToast();
    return true;
  }
  return false;
}

/**
 * The same refusals on a retry of an already-kept capture: 423 → back to
 * `held` (the next re-auth retries), 429 → `rate_limited` with a new `retryAt`
 * and a rescheduled retry, 503 UNAVAILABLE → `unavailable` (same timed retry),
 * 402 → `needs_credits`, 241 → `needs_plan`. Returns false otherwise, and
 * the caller marks the entry `failed`.
 */
function applyRetryRefusal(id: string, err: unknown): boolean {
  if (isSubscriptionRefusal(err)) notifySubscriptionRequired();
  let status: 'held' | 'rate_limited' | 'needs_credits' | 'needs_plan' | 'unavailable';
  let retryAt: number | undefined;
  if (err instanceof SessionLockedError) status = 'held';
  else if (err instanceof RateLimitedError) {
    status = 'rate_limited';
    retryAt = Date.now() + err.retryAfterSecs * 1000;
  } else if (err instanceof UnavailableError) {
    status = 'unavailable';
    retryAt = Date.now() + err.retryAfterSecs * 1000;
  } else if (isCreditsRefusal(err)) status = 'needs_credits';
  else if (isPlanRefusal(err)) status = 'needs_plan';
  else return false;
  useRecordingsStore.setState((s) => {
    const p = s.pendingUploads.find((x) => x.id === id);
    if (!p) return;
    p.status = status;
    if (retryAt !== undefined) p.retryAt = retryAt;
  });
  if (retryAt !== undefined) {
    scheduleRateLimitedRetry(id, retryAt);
    // An `unavailable` retry repeats every minute while the outage lasts; the
    // banner line shows the next attempt, so only the first hold toasts.
    if (status === 'rate_limited') showRateLimitedToast(retryAt);
  } else if (status === 'needs_credits') {
    showCreditsHeldToast();
  } else if (status === 'needs_plan') {
    showPlanHeldToast();
  }
  return true;
}

/**
 * An import has no capture to keep (the server re-fetches the URL), so a 402 or
 * 429 only tells the user what to do. Returns false for every other failure.
 */
function importRefusalToast(err: unknown): boolean {
  if (isSubscriptionRefusal(err)) notifySubscriptionRequired();
  if (isCreditsRefusal(err)) {
    showCreditsExhaustedToast();
    return true;
  }
  if (err instanceof RateLimitedError) {
    toast.warning(tRec('rateLimitedTitle', 'Upload limit reached'), {
      description: tRec(
        'importRateLimitedDescription',
        'Too many imports in the last hour. Try again in about {minutes} min.',
        { minutes: Math.max(1, Math.ceil(err.retryAfterSecs / 60)) },
      ),
    });
    return true;
  }
  return false;
}

/**
 * BILL-05: a WS retry flips the row to `pending` optimistically, but a paid
 * retry can now be refused with 402 BUDGET_EXHAUSTED, which leaves the server
 * row untouched. The refusal arrives as a bare WS error envelope with no
 * request id, so the pre-flip row is kept here per recording (never a single
 * slot) and restored when a 402 lands while a retry is unanswered. Entries are
 * dropped on the retry's response or status event, and ignored once older than
 * RETRY_ROLLBACK_MS — the status poll repaints the truth after that anyway.
 */
const _retryRollback = new Map<string, { rec: Recording; at: number }>();
const RETRY_ROLLBACK_MS = 15_000;

/**
 * Module-level handle for the list-load timeout. `fetchList` sets it after
 * `sendListRecordings()`; if the WS response never arrives (dropped socket,
 * server crash) the timer flips `loadState` to `'error'` so the UI shows a
 * retryable error instead of a permanent loading skeleton (CLAUDE.md: "WS
 * load states must have a timeout path to 'error'"). It's a single slot
 * because the list fetch is itself a singleton (the response overwrites
 * whatever a more-recent fetch set — see the `fetchList` note); cleared in
 * `handleListResponse` and on the recordings WS error envelope.
 */
let listLoadTimer: ReturnType<typeof setTimeout> | null = null;

/** How long we wait for a ListRecordingsResponse before declaring an error. */
const LIST_LOAD_TIMEOUT_MS = 30_000;

/**
 * In-flight retry ids (module-level, like `_pendingBlobs`). Guards against two
 * concurrent `retryAllPendingUploads` calls (rapid re-auth) both passing the
 * async `status==='retrying'` check and double-uploading the same blob. Checked
 * and added synchronously before any await; removed in `finally`.
 */
const retryingIds = new Set<string>();

/**
 * Per-id ceiling timers for an in-flight cancel. A `Map` keyed by recording id,
 * NOT a single slot: two concurrent cancels would otherwise clobber each
 * other's timer and one button would stay disabled forever (CLAUDE.md,
 * "single-slot flags for request correlation are a race condition").
 */
const _cancelTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * How long a Cancel button stays disabled with no answer at all. Mirrors
 * LIST_LOAD_TIMEOUT_MS: a dropped socket must not leave a permanently inert
 * control. Every normal outcome — the response, or the terminal status event
 * that caused a refusal — releases it far sooner.
 */
const CANCEL_PENDING_TIMEOUT_MS = 30_000;

/** (Re)arm the ceiling for `id`, superseding any previous one. */
function armCancelCeiling(id: string, onExpire: () => void): void {
  disarmCancelCeiling(id);
  _cancelTimers.set(id, setTimeout(onExpire, CANCEL_PENDING_TIMEOUT_MS));
}

/** Drop `id`'s ceiling timer if one is armed. Idempotent. */
function disarmCancelCeiling(id: string): void {
  const timer = _cancelTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    _cancelTimers.delete(id);
  }
}

/**
 * Mark `id` as having a cancel in flight and arm its ceiling. Returns false if
 * one was already in flight — that IS the duplicate-click guard, so the caller
 * must not send when it returns false. The ceiling is armed before the send so
 * a throwing send cannot strand the button disabled.
 */
function beginCancelPending(id: string): boolean {
  if (useRecordingsStore.getState().cancelPending.has(id)) return false;
  useRecordingsStore.setState((s) => {
    s.cancelPending.add(id);
  });
  armCancelCeiling(id, () => releaseCancelPending(id));
  return true;
}

/**
 * Release `id`'s pending-cancel flag and its ceiling. Module-level rather than
 * a store action: it is internal bookkeeping with four callers (the response,
 * the terminal status event, any terminal upsert, and the ceiling itself), and
 * nothing outside this file should be able to lie about it. Idempotent.
 */
function releaseCancelPending(id: string): void {
  disarmCancelCeiling(id);
  if (!useRecordingsStore.getState().cancelPending.has(id)) return;
  useRecordingsStore.setState((s) => {
    s.cancelPending.delete(id);
  });
}

/**
 * `held` — session locked (423); `needs_credits` — refused for credits (402);
 * `rate_limited` — over the per-user upload limit (429), retried once at
 * `retryAt`; `needs_plan` — refused for the plan (241, BILL-20f): the user
 * upgrades on /plans in a new tab and retries from the banner; `unavailable` —
 * the tier could not be read (503 UNAVAILABLE, BILL-20f P1), retried once at
 * `retryAt` like `rate_limited`. All five keep the capture on the device (BILL-05).
 */
export type PendingUploadStatus =
  | 'held'
  | 'retrying'
  | 'failed'
  | 'needs_credits'
  | 'rate_limited'
  | 'needs_plan'
  | 'unavailable';

export interface PendingUpload {
  id: string;
  title: string;
  sizeBytes: number;
  capturedAt: number;
  status: PendingUploadStatus;
  error?: string;
  /** The duration hint the capture was made with; every retry resends it (BILL-05). */
  durationSecs?: number;
  /** `rate_limited` / `unavailable` only: epoch ms before which no retry is sent (`Retry-After`). */
  retryAt?: number;
}

/** A `rate_limited` / `unavailable` capture whose `Retry-After` has not yet elapsed (BILL-05, BILL-20f P1). */
function awaitingRetryAt(p: PendingUpload, now: number): boolean {
  return (p.status === 'rate_limited' || p.status === 'unavailable') && p.retryAt !== undefined && p.retryAt > now;
}

interface RecordingsState {
  /** Map keyed by recordingId for O(1) upsert; UI selectors materialise an array. */
  recordings: Map<string, Recording>;
  loadState: LoadState;
  errorMessage: string | null;

  /**
   * Captures held locally because the session was locked at upload time.
   * Metadata only — the bytes live in the module-level `_pendingBlobs`.
   */
  pendingUploads: PendingUpload[];

  /** Per-recording stuck-status timers. NOT a module-level slot. */
  _statusTimers: Map<string, ReturnType<typeof setTimeout>>;

  /**
   * Recording ids with a cancel request in flight.
   *
   * `cancelTranscription` is deliberately NOT optimistic, unlike `retry` — but
   * not for the reason an earlier draft of that comment gave. A refused cancel
   * is always accompanied by the terminal status event that CAUSED the refusal
   * (`ready` ⇒ Orchestrator.Run's success write fires StatusReady; `failed` ⇒
   * setStatusUnlessCancelled fires it; already `cancelled` ⇒ the earlier cancel
   * went through UpdateAndNotify), so the row self-heals either way. The reason
   * to skip the optimistic flip is simpler: the authoritative row is one
   * round-trip away, and a flip-to-cancelled-then-back-to-ready reads as a bug.
   *
   * What the round-trip DOES need is press feedback, because `status` does not
   * move — `canCancelTranscription` still says "cancellable" and the button
   * renders identically until the answer lands, so an impatient user clicks
   * again and the server answers the second click with an error. This set
   * disables the button, swaps in a spinner, and makes that second click a
   * no-op.
   *
   * Reactive (not module-level like `retryingIds`) because two components
   * render from it. A `Set`, per CLAUDE.md — a single slot breaks the moment
   * two rows are cancelled at once.
   */
  cancelPending: Set<string>;

  actions: {
    fetchList: () => void;
    upsert: (rec: Recording) => void;
    remove: (id: string) => void;
    applyStatusEvent: (ev: RecordingStatusEvent) => void;
    scheduleStatusTimeout: (id: string) => void;
    clearStatusTimer: (id: string) => void;

    rename: (id: string, fields: UpdateRecordingFields) => void;
    setSelfSpeaker: (id: string, label: string, isSelf: boolean) => Promise<void>;
    retry: (id: string) => void;
    cancelTranscription: (id: string) => void;
    promote: (id: string) => void;
    destroy: (id: string) => void;

    /** `durationSecs` is the BILL-05 hint; without one the file metadata is read. */
    upload: (file: File, title?: string, durationSecs?: number) => Promise<Recording | null>;
    uploadFromURL: (url: string, title?: string) => Promise<Recording | null>;
    uploadFromMic: (blob: Blob, title?: string, durationSecs?: number) => Promise<Recording | null>;

    // Session-lock recovery: hold a capture for retry, retry one/all, discard.
    retryPendingUpload: (id: string) => Promise<void>;
    retryAllPendingUploads: () => void;
    discardPendingUpload: (id: string) => void;

    // WS message handlers
    handleListResponse: (env: MessageEnvelope) => void;
    handleGetResponse: (env: MessageEnvelope) => void;
    handleUpdateResponse: (env: MessageEnvelope) => void;
    handleDeleteResponse: (env: MessageEnvelope) => void;
    handleRetryResponse: (env: MessageEnvelope) => void;
    handleCancelResponse: (env: MessageEnvelope) => void;
    handlePromoteResponse: (env: MessageEnvelope) => void;
    handleStatusEvent: (env: MessageEnvelope) => void;
  };
}

export const useRecordingsStore = create<RecordingsState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        recordings: new Map(),
        loadState: 'idle',
        errorMessage: null,
        pendingUploads: [],
        _statusTimers: new Map(),
        cancelPending: new Set<string>(),

        actions: {
          fetchList: () => {
            // NOTE: the WS manager does not honour AbortSignal — the
            // previous AbortController-based supersede was inert
            // (controller created, signal never threaded, response always
            // applied). A real request-id correlation lives at the
            // envelope layer and the wsManager doesn't yet expose it for
            // recordings. The current contract: a late ListRecordingsResponse
            // simply overwrites whatever was set by a more-recent fetch;
            // since lists are idempotent and small, the practical fallout
            // is a brief "older list flashes over newer" if two fetches
            // race — acceptable for v1.
            set((s) => {
              s.loadState = 'loading';
              s.errorMessage = null;
            });
            sendListRecordings();
            // Timeout path to 'error' (CLAUDE.md): a dropped WS would otherwise
            // leave loadState stuck at 'loading' forever (permanent skeleton).
            // Single slot — the list fetch is a singleton, so a newer fetch's
            // timer simply supersedes the prior one (which we clear first).
            if (listLoadTimer) clearTimeout(listLoadTimer);
            listLoadTimer = setTimeout(() => {
              listLoadTimer = null;
              if (get().loadState === 'loading') {
                set((s) => {
                  s.loadState = 'error';
                  s.errorMessage = tRec('loadTimeout', 'Timed out loading recordings');
                });
              }
            }, LIST_LOAD_TIMEOUT_MS);
          },

          upsert: (rec: Recording) => {
            set((s) => {
              s.recordings.set(rec.recordingId, rec);
            });
            // Schedule / clear the stuck-status timer based on the new status.
            if (IN_FLIGHT_STATUSES.has(rec.status)) {
              get().actions.scheduleStatusTimeout(rec.recordingId);
            } else {
              get().actions.clearStatusTimer(rec.recordingId);
              releaseCancelPending(rec.recordingId); // the row settled, either way
            }
          },

          remove: (id: string) => {
            get().actions.clearStatusTimer(id);
            // A row deleted mid-cancel must release its bookkeeping too, or the
            // id sits in `cancelPending` with a 30s ceiling armed against a row
            // that no longer exists. Every other lifecycle path releases
            // deliberately; this is the fourth.
            releaseCancelPending(id);
            set((s) => {
              s.recordings.delete(id);
            });
          },

          applyStatusEvent: (ev: RecordingStatusEvent) => {
            // The proto-gen Recording is a class instance. Mutating it in
            // place inside an immer producer revokes the draft proxy on the
            // next access — "illegal operation attempted on a revoked proxy"
            // surfaces as a full-screen error. Replace the Map entry with a
            // plain shallow copy + the new fields so immer just sees a
            // pointer change.
            set((s) => {
              const old = s.recordings.get(ev.recordingId);
              if (!old) return;
              const next: Recording = {
                ...old,
                status: ev.status,
                failureReason: resolveFailureReason(ev.status, ev.failureReason, old.failureReason),
              };
              s.recordings.set(ev.recordingId, next);
            });
            if (IN_FLIGHT_STATUSES.has(ev.status)) {
              get().actions.scheduleStatusTimeout(ev.recordingId);
            } else {
              get().actions.clearStatusTimer(ev.recordingId);
              releaseCancelPending(ev.recordingId); // incl. a REFUSED cancel
            }
            // Re-fetch on a terminal transition so the row shows the
            // freshly-populated metadata (see TERMINAL_REFETCH_STATUSES).
            if (TERMINAL_REFETCH_STATUSES.has(ev.status)) sendListRecordings();
          },

          scheduleStatusTimeout: (id: string) => {
            // This is a *liveness poll*, not a transcription deadline. Long
            // audio (whisper + diarization) legitimately runs for minutes;
            // the server — not the client — decides when it's done. So on
            // each tick we re-fetch authoritative state rather than fabricate
            // a `failed` the server never reported (the old behaviour flipped
            // every in-flight row to "Timed out…" after a flat 30 s, which
            // false-alarmed on any long recording and then "un-failed" when
            // the real result arrived). We only declare failure after the
            // ceiling, when the server has genuinely gone silent.
            const existing = get()._statusTimers.get(id);
            if (existing) clearTimeout(existing);
            if (!_watchStartedAt.has(id)) _watchStartedAt.set(id, Date.now());

            const timer = setTimeout(() => {
              // Drop our own slot first so re-entrancy below is clean.
              set((s) => {
                s._statusTimers.delete(id);
              });

              const rec = get().recordings.get(id);
              if (!rec || !IN_FLIGHT_STATUSES.has(rec.status)) {
                _watchStartedAt.delete(id);
                return;
              }

              const startedAt = _watchStartedAt.get(id) ?? Date.now();
              if (Date.now() - startedAt < statusMaxWaitMs()) {
                // Within the realistic window: re-poll authoritative state and
                // keep watching. handleListResponse will reschedule on arrival;
                // we also reschedule here so the ceiling still fires if the
                // server never answers.
                sendListRecordings();
                get().actions.scheduleStatusTimeout(id);
                return;
              }

              // Past the server's max wait + margin and still in-flight with no
              // terminal event — genuinely stuck. An error is now honest. The
              // row stays recoverable via Retry.
              _watchStartedAt.delete(id);
              const limitMin = Math.round(statusMaxWaitMs() / 60_000);
              set((s) => {
                const r = s.recordings.get(id);
                if (r && IN_FLIGHT_STATUSES.has(r.status)) {
                  s.recordings.set(id, {
                    ...r,
                    status: 'failed',
                    failureReason:
                      r.failureReason ||
                      tRec(
                        'transcriptionTimeout',
                        `Transcription timed out after ~${limitMin} min — the server stopped responding. Try Retry.`,
                        { minutes: limitMin },
                      ),
                  });
                }
              });
            }, STATUS_POLL_MS);

            set((s) => {
              s._statusTimers.set(id, timer);
            });
          },

          clearStatusTimer: (id: string) => {
            _watchStartedAt.delete(id);
            const existing = get()._statusTimers.get(id);
            if (!existing) return;
            clearTimeout(existing);
            set((s) => {
              s._statusTimers.delete(id);
            });
          },

          rename: (id: string, fields: UpdateRecordingFields) => {
            sendUpdateRecording(id, fields);
          },

          // Speaker-recognition active learning: mark/clear a diarized cluster
          // as the account holder. The endpoint enrolls/down-weights the
          // voiceprint server-side and returns the updated recording (proto-JSON
          // shape, with the new self_speaker_label). We funnel that response
          // through the SAME `upsert` sink the WS rename path uses
          // (handleUpdateResponse → actions.upsert), so the badge re-renders and
          // the stuck-status timers reschedule identically — the only difference
          // is the response arrives over REST rather than a WS envelope. `id` is
          // threaded the whole way, so two concurrent corrections on different
          // recordings can't clobber each other (no single-slot flag).
          setSelfSpeaker: async (id: string, label: string, isSelf: boolean): Promise<void> => {
            try {
              const rec = await apiSetSelfSpeaker(id, label, isSelf);
              get().actions.upsert(rec);
            } catch (err) {
              // Secondary affordance — never throw into render. Route through the
              // shared warm/safe toast (same path `upload`/`uploadFromURL` use);
              // the recording is unchanged, so the prior badge state stands.
              const wire: WireError = err instanceof HttpError ? err.wire : {};
              useToastStore.getState().actions.showError(wire);
            }
          },

          retry: (id: string) => {
            const prior = get().recordings.get(id);
            if (prior) _retryRollback.set(id, { rec: { ...prior }, at: Date.now() });
            sendRetryTranscription(id);
            // Optimistic — flip back to pending and drop the embedded
            // transcript so the player doesn't show stale text between
            // status:'pending' and the new fetch arriving. Replace the Map
            // entry with a plain shallow copy (immer's draft proxy on the
            // proto-gen class instance can revoke and throw on the next
            // access).
            set((s) => {
              const rec = s.recordings.get(id);
              if (!rec) return;
              const next: Recording = {
                ...rec,
                status: 'pending',
                failureReason: '',
              };
              delete (next as { transcript?: unknown }).transcript;
              s.recordings.set(id, next);
            });
            get().actions.scheduleStatusTimeout(id);
          },

          // Stop an in-flight transcription (REC-CANCEL-01). Non-optimistic on
          // purpose, plus a pending flag for press feedback — rationale on the
          // `cancelPending` state field; the send is gated on the flag, which
          // is what makes an impatient second click a no-op.
          cancelTranscription: (id: string) => {
            if (beginCancelPending(id)) sendCancelTranscription(id);
          },

          promote: (id: string) => {
            sendPromoteRecording(id);
          },

          destroy: (id: string) => {
            sendDeleteRecording(id);
            // Optimistic remove
            get().actions.remove(id);
          },

          upload: async (file: File, title?: string, durationSecs?: number): Promise<Recording | null> => {
            const placeholderId = `uploading-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const optimistic: Recording = {
              $typeName: 'lvdr.v2.recordings.Recording',
              recordingId: placeholderId,
              ownerId: '',
              title: title || file.name,
              source: 'upload',
              sourceUrl: '',
              originalName: file.name,
              mime: file.type,
              durationSecs: 0,
              sizeBytes: BigInt(file.size),
              status: 'uploading',
              failureReason: '',
              speakers: [],
              tags: [],
              collectionId: '',
              promotedDocId: '',
              asrModel: '',
              costUsd: 0,
              costCredits: BigInt(0),
            } as unknown as Recording;

            set((s) => {
              s.recordings.set(placeholderId, optimistic);
            });

            // BILL-05 duration hint. Computed BEFORE the `try` so every hold
            // branch in the `catch` keeps it with the capture — a held
            // file-picker upload must resend the metadata hint on retry.
            // readAudioDurationSecs never rejects.
            const hint = durationSecs ?? (await readAudioDurationSecs(file));

            try {
              const wsId = activeWorkspaceId();
              const rec = await uploadRecording(file, {
                ...(title ? { title } : {}),
                ...(wsId ? { workspaceId: wsId } : {}),
                ...(hint !== undefined ? { durationSecs: hint } : {}),
              });
              set((s) => {
                s.recordings.delete(placeholderId);
                s.recordings.set(rec.recordingId, rec);
              });
              if (IN_FLIGHT_STATUSES.has(rec.status)) {
                get().actions.scheduleStatusTimeout(rec.recordingId);
              }
              return rec;
            } catch (err) {
              set((s) => {
                s.recordings.delete(placeholderId);
              });
              // 423 / 429 / 503 / 402 / 241 keep the capture on the device (see holdRefusedUpload).
              if (holdRefusedUpload(file, title, hint, err)) return null;
              // R7: route other failures through the single warm/safe error
              // path. The typed wire error (from httpWireError) drives the
              // catalog message + the retryable "Try again" affordance; a retry
              // re-runs the upload.
              const wire: WireError = err instanceof HttpError ? err.wire : {};
              useToastStore.getState().actions.showError(wire, {
                onRetry: () => {
                  void get().actions.upload(file, title, hint);
                },
              });
              return null;
            }
          },

          retryPendingUpload: async (id: string): Promise<void> => {
            const blob = _pendingBlobs.get(id);
            const entry = get().pendingUploads.find((p) => p.id === id);
            if (!blob || !entry) {
              // Stale entry (blob lost on reload, or already cleared): drop it.
              clearRateLimitTimer(id);
              _pendingBlobs.delete(id);
              set((s) => {
                s.pendingUploads = s.pendingUploads.filter((p) => p.id !== id);
              });
              return;
            }
            if (entry.status === 'retrying') return; // already in flight
            // BILL-05: no retry before Retry-After elapses — the scheduled
            // retry clears `retryAt` before it calls in.
            if (awaitingRetryAt(entry, Date.now())) {
              return;
            }
            // Synchronous in-flight guard: two concurrent retryAllPendingUploads
            // calls (rapid re-auth) can both pass the async status check above
            // before either set('retrying') commits, double-uploading the same
            // blob. The module-level Set is checked+added synchronously here,
            // before any await, and removed in the finally below.
            if (retryingIds.has(id)) return;
            retryingIds.add(id);
            clearRateLimitTimer(id);

            try {
              set((s) => {
                const p = s.pendingUploads.find((x) => x.id === id);
                if (p) {
                  p.status = 'retrying';
                  delete p.error;
                  delete p.retryAt;
                }
              });

              const file =
                blob instanceof File
                  ? blob
                  : new File([blob], entry.title, { type: blob.type || 'audio/webm' });
              try {
                const wsId = activeWorkspaceId();
                const rec = await uploadRecording(file, {
                  ...(entry.title ? { title: entry.title } : {}),
                  ...(wsId ? { workspaceId: wsId } : {}),
                  ...(entry.durationSecs !== undefined ? { durationSecs: entry.durationSecs } : {}),
                });
                _pendingBlobs.delete(id);
                set((s) => {
                  s.pendingUploads = s.pendingUploads.filter((p) => p.id !== id);
                  s.recordings.set(rec.recordingId, rec);
                });
                if (IN_FLIGHT_STATUSES.has(rec.status)) {
                  get().actions.scheduleStatusTimeout(rec.recordingId);
                }
                toast.success(tRec('uploaded', 'Recording uploaded'), { description: entry.title });
              } catch (err) {
                // 423 / 429 / 503 / 402 / 241 keep the capture (see applyRetryRefusal).
                if (applyRetryRefusal(id, err)) return;
                const msg = err instanceof Error ? err.message : tRec('uploadFailed', 'Upload failed');
                set((s) => {
                  const p = s.pendingUploads.find((x) => x.id === id);
                  if (p) {
                    p.status = 'failed';
                    p.error = msg;
                  }
                });
                toast.error(tRec('uploadFailed', 'Upload failed'), { description: msg });
              }
            } finally {
              retryingIds.delete(id);
            }
          },

          retryAllPendingUploads: () => {
            // Snapshot ids first; retryPendingUpload mutates the array.
            // BILL-05: a `needs_credits` capture waits for the user to top up
            // (re-auth does not add credits), and a `rate_limited` one waits for
            // its Retry-After — its own scheduled retry covers it. BILL-20f: a
            // `needs_plan` capture waits for an upgrade (re-auth does not change the plan).
            const now = Date.now();
            const ids = get()
              .pendingUploads.filter(
                (p) =>
                  p.status !== 'retrying' &&
                  p.status !== 'needs_credits' &&
                  p.status !== 'needs_plan' &&
                  !awaitingRetryAt(p, now),
              )
              .map((p) => p.id);
            for (const id of ids) {
              void get().actions.retryPendingUpload(id);
            }
          },

          discardPendingUpload: (id: string) => {
            clearRateLimitTimer(id);
            _pendingBlobs.delete(id);
            set((s) => {
              s.pendingUploads = s.pendingUploads.filter((p) => p.id !== id);
            });
          },

          uploadFromURL: async (url: string, title?: string): Promise<Recording | null> => {
            try {
              const rec = await importFromURL(url, title, undefined, activeWorkspaceId());
              set((s) => {
                s.recordings.set(rec.recordingId, rec);
              });
              if (IN_FLIGHT_STATUSES.has(rec.status)) {
                get().actions.scheduleStatusTimeout(rec.recordingId);
              }
              return rec;
            } catch (err) {
              // Session-lock parity with the multipart upload path: a 423 means
              // the bytes are fine but the server-side DEK is missing. Unlike a
              // mic capture we have no blob to hold (URLs are re-fetched
              // server-side), so we tell the user to unlock and retry rather
              // than surfacing a generic wire error.
              if (err instanceof SessionLockedError) {
                toast.error(tRec('importLockedTitle', 'Session locked'), {
                  description: tRec('importLockedDescription', 'Unlock your session, then try the import again.'),
                });
                return null;
              }
              if (importRefusalToast(err)) return null;
              const wire: WireError = err instanceof HttpError ? err.wire : {};
              useToastStore.getState().actions.showError(wire, {
                onRetry: () => {
                  void get().actions.uploadFromURL(url, title);
                },
              });
              return null;
            }
          },

          uploadFromMic: async (blob: Blob, title?: string, durationSecs?: number): Promise<Recording | null> => {
            // Wrap the blob as a File so the multipart endpoint can name it.
            const ext = blob.type.includes('webm')
              ? '.webm'
              : blob.type.includes('mp4')
                ? '.mp4'
                : blob.type.includes('ogg')
                  ? '.ogg'
                  : '.webm';
            const fname = `recording-${new Date().toISOString().replace(/[:.]/g, '-')}${ext}`;
            const file = new File([blob], fname, { type: blob.type || 'audio/webm' });
            return await get().actions.upload(file, title, durationSecs);
          },

          // ---------- WS handlers ----------

          handleListResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'listRecordingsResponse') return;
            // Response arrived — cancel the pending timeout-to-error.
            if (listLoadTimer) {
              clearTimeout(listLoadTimer);
              listLoadTimer = null;
            }
            const value = env.payload.value as ListRecordingsResponse;
            set((s) => {
              s.recordings = new Map(value.recordings.map((r) => [r.recordingId, r]));
              s.loadState = 'ready';
              s.errorMessage = null;
            });
            // Reschedule timers for any in-flight rows.
            const acts = get().actions;
            for (const r of value.recordings) {
              if (IN_FLIGHT_STATUSES.has(r.status)) acts.scheduleStatusTimeout(r.recordingId);
            }
          },

          handleGetResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'getRecordingResponse') return;
            const value = env.payload.value as GetRecordingResponse;
            if (value.recording) get().actions.upsert(value.recording);
          },

          handleUpdateResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'updateRecordingResponse') return;
            const value = env.payload.value as UpdateRecordingResponse;
            if (value.recording) get().actions.upsert(value.recording);
          },

          handleDeleteResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'deleteRecordingResponse') return;
            const value = env.payload.value as DeleteRecordingResponse;
            if (value.recordingId) get().actions.remove(value.recordingId);
          },

          handleRetryResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'retryTranscriptionResponse') return;
            const value = env.payload.value as RetryTranscriptionResponse;
            if (value.recording) {
              _retryRollback.delete(value.recording.recordingId);
              get().actions.upsert(value.recording);
            }
          },

          handleCancelResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'cancelTranscriptionResponse') return;
            const value = env.payload.value as CancelTranscriptionResponse;
            if (!value.recording) return;
            releaseCancelPending(value.recording.recordingId);
            get().actions.upsert(value.recording);
          },

          handlePromoteResponse: (env: MessageEnvelope) => {
            if (env.payload.case !== 'promoteRecordingResponse') return;
            const value = env.payload.value as PromoteRecordingResponse;
            if (value.documentId) {
              toast.success(tRec('promotedTitle', 'Added to library'), {
                description: tRec('promotedDescription', 'You can find it on the Library page.'),
              });
              // The owning row already has promotedDocId set server-side and
              // will arrive via the next list refresh or a status event.
            }
          },

          handleStatusEvent: (env: MessageEnvelope) => {
            if (env.payload.case !== 'recordingStatusEvent') return;
            const ev = env.payload.value as RecordingStatusEvent;
            _retryRollback.delete(ev.recordingId);
            get().actions.applyStatusEvent(ev);
          },
        },
      })),
    ),
    { name: 'RecordingsStore' },
  ),
);

/** Restore every unanswered retry's pre-flip row (see `_retryRollback`). */
function rollBackRefusedRetries(): void {
  const now = Date.now();
  const restore: Recording[] = [];
  for (const [id, entry] of _retryRollback) {
    if (now - entry.at <= RETRY_ROLLBACK_MS) restore.push(entry.rec);
    _retryRollback.delete(id);
  }
  if (restore.length === 0) return;
  const { actions } = useRecordingsStore.getState();
  useRecordingsStore.setState((s) => {
    for (const rec of restore) s.recordings.set(rec.recordingId, rec);
  });
  for (const rec of restore) actions.clearStatusTimer(rec.recordingId);
}

/**
 * Register WebSocket handlers for recording messages. Call once on app
 * init (alongside the other `register*Handlers` in `AppShell`). Returns
 * an unsubscribe.
 */
export function registerRecordingHandlers(): () => void {
  const { actions } = useRecordingsStore.getState();
  const unsubs = [
    wsManager.onMessage('listRecordingsResponse', actions.handleListResponse),
    wsManager.onMessage('getRecordingResponse', actions.handleGetResponse),
    wsManager.onMessage('updateRecordingResponse', actions.handleUpdateResponse),
    wsManager.onMessage('deleteRecordingResponse', actions.handleDeleteResponse),
    wsManager.onMessage('retryTranscriptionResponse', actions.handleRetryResponse),
    wsManager.onMessage('cancelTranscriptionResponse', actions.handleCancelResponse),
    wsManager.onMessage('promoteRecordingResponse', actions.handlePromoteResponse),
    wsManager.onMessage('recordingStatusEvent', actions.handleStatusEvent),
    // A WS-level error while a list load is in flight should fail fast to
    // 'error' rather than wait out the 30s timeout (CLAUDE.md: "WS load
    // states must have a timeout path to 'error'" — the error envelope is
    // the prompt path; the timer is the silent-drop backstop).
    // BILL-05 / BILL-20f: a 402 or a 241 plan refusal while a retry is
    // unanswered undoes its optimistic flip.
    wsManager.onError((code, _message, wire) => {
      if (code === 402 || isPlanUpgradeRequired(wire)) rollBackRefusedRetries();
    }),
    wsManager.onError(() => {
      if (useRecordingsStore.getState().loadState !== 'loading') return;
      if (listLoadTimer) {
        clearTimeout(listLoadTimer);
        listLoadTimer = null;
      }
      useRecordingsStore.setState((s) => {
        s.loadState = 'error';
        s.errorMessage = 'Connection error while loading recordings';
      });
    }),
  ];
  return () => {
    for (const u of unsubs) u();
  };
}

/**
 * Selector returning a recording-array sorted newest first. We memoise on the
 * `Map` reference (immer produces a new Map on each relevant mutation) so
 * subsequent selector reads return the same array identity — Zustand v5
 * otherwise treats a fresh sorted array as a state change and re-renders.
 *
 * The memoisation cache lives in component-local `useMemo` rather than at
 * module scope: module-level state would leak across user sessions (a stale
 * sorted array from user A surviving into user B's session in the same JS
 * runtime, e.g. after lock/unlock) and would also be shared across all
 * subscribers in the same module, which can produce subtle inconsistencies
 * if two trees subscribe in different orders.
 */
export function useRecordingsList(): Recording[] {
  const recordings = useRecordingsStore((s) => s.recordings);
  return React.useMemo(() => {
    const arr = Array.from(recordings.values());
    arr.sort((a, b) => {
      const aSec = Number(a.createdAt?.seconds ?? BigInt(0));
      const bSec = Number(b.createdAt?.seconds ?? BigInt(0));
      return bSec - aSec;
    });
    return arr;
  }, [recordings]);
}
